// Tiempos de reporte de Monitor (mantener sincronizado con
// InstancesMonitorController del backend).
// Monitor reporta cada REPORT_INTERVAL_MINUTES; se toma como atrasado recién
// pasado STALE_REPORT_MINUTES (intervalo + margen) para no marcar atrasado un
// reporte que está en camino.
export const REPORT_INTERVAL_MINUTES = 30
export const STALE_GRACE_MINUTES = 5
export const STALE_REPORT_MINUTES = REPORT_INTERVAL_MINUTES + STALE_GRACE_MINUTES

// estado del registro respecto de Monitor:
//  pending = sin emparejar; never = emparejado sin reportes;
//  stale = reporte atrasado; ok = al día
export function reportState(item) {
  if(!item || !item.monitor_id) return 'pending'
  if(!item.monitor_reported_at) return 'never'
  return minutesSince(item.monitor_reported_at) > STALE_REPORT_MINUTES ? 'stale' : 'ok'
}

// fecha sola (Y-m-d), sin hora
function isDateOnly(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value))
}

export function parseDate(value) {
  if(!value) return null
  // una fecha sola se toma en hora local (new Date('Y-m-d') la toma en UTC y corre el día)
  var date = new Date(isDateOnly(value) ? value + 'T00:00' : String(value).replace(' ', 'T'))
  return isNaN(date.getTime()) ? null : date
}

export function minutesSince(value) {
  var date = parseDate(value)
  return date ? Math.round((Date.now() - date.getTime()) / 60000) : null
}

// duración en lenguaje humano: "12 minutos", "1 hora y 20 minutos",
// "5 horas", "2 días y 3 horas", "9 días"
export function humanDuration(minutes) {
  var total = Math.abs(Math.round(minutes))
  var plural = (value, one, many) => value + ' ' + (value == 1 ? one : many)
  if(total < 1) return 'un instante'
  if(total < 60) return plural(total, 'minuto', 'minutos')

  var hours = Math.floor(total / 60)
  if(hours < 24) {
    var restMinutes = total % 60
    // el detalle de minutos solo aporta en las primeras horas
    return plural(hours, 'hora', 'horas') + (hours < 6 && restMinutes >= 5 ? ' y ' + plural(restMinutes, 'minuto', 'minutos') : '')
  }

  var days = Math.floor(hours / 24)
  if(days >= 365) return plural(Math.floor(days / 365), 'año', 'años')
  if(days >= 60) return plural(Math.floor(days / 30), 'mes', 'meses')
  var restHours = hours % 24
  return plural(days, 'día', 'días') + (days < 3 && restHours > 0 ? ' y ' + plural(restHours, 'hora', 'horas') : '')
}

// antigüedad de la última actualización de una versión (sistema / intranet):
// hasta VERSION_FRESH_DAYS = al día, hasta VERSION_OLD_DAYS = a revisar, más = vieja
export const VERSION_FRESH_DAYS = 30
export const VERSION_OLD_DAYS = 90

export function versionAge(value) {
  var minutes = minutesSince(value)
  if(minutes === null) return null
  var days = minutes / 1440
  return {
    label: minutes < 1 ? 'recién' : humanDuration(minutes),
    tone: days <= VERSION_FRESH_DAYS ? 'success' : (days <= VERSION_OLD_DAYS ? 'warning' : 'danger'),
  }
}

// fecha de la versión de sistema / intranet: fecha_version si Monitor la manda;
// si no, la que figure en el texto de la versión ("bfc1fbdf1 - 2026-06-03 - ...")
export function versionDate(app) {
  if(!app) return null
  if(app.fecha_version) return app.fecha_version
  var match = String(app.version || '').match(/(^|[^\d])(\d{4}-\d{2}-\d{2})(?!\d)/)
  return match && parseDate(match[2]) ? match[2] : null
}

// uso de recursos del servidor (CPU, RAM, disco): verde / amarillo / rojo
export const RESOURCE_WARN_PERCENT = 70
export const RESOURCE_CRITICAL_PERCENT = 90

export function resourceTone(value) {
  if(value >= RESOURCE_CRITICAL_PERCENT) return 'danger'
  if(value >= RESOURCE_WARN_PERCENT) return 'warning'
  return 'success'
}

// servidor con algún recurso en zona crítica
export function resourcesCritical(item) {
  var data = parseJson(item && item.monitor_recursos)
  if(!data || typeof data != 'object') return false
  return ['cpu', 'ram', 'disco'].some(key => data[key] && data[key].uso >= RESOURCE_CRITICAL_PERCENT)
}

// fechas siempre en d/m/Y (con hora si la tiene)
export function formatDate(value, withTime) {
  var date = parseDate(value)
  if(!date) return value || ''
  var pad = n => String(n).padStart(2, '0')
  var text = pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear()
  return withTime === false || isDateOnly(value) ? text : text + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes())
}

// "hace 12 minutos", "hace 2 días y 3 horas"
export function timeAgo(value) {
  var minutes = minutesSince(value)
  if(minutes === null) return ''
  if(minutes < 1) return 'recién'
  return 'hace ' + humanDuration(minutes)
}

export function parseJson(value) {
  if(value === null || value === undefined || value === '') return null
  if(typeof value != 'string') return value
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

export function countAvisos(item) {
  var avisos = parseJson(item && item.monitor_avisos)
  return Array.isArray(avisos) ? avisos.length : 0
}

// costo del servidor: frecuencia de pago en meses según el servicio vinculado
// (mode/renewal del servicio). null si el servicio no tiene período (por unidad)
export function serviceCostMonths(service) {
  if(!service || !(parseInt(service.renewal) > 0)) return null
  var renewal = parseInt(service.renewal)
  if(service.mode == 'months') return renewal
  if(service.mode == 'years') return renewal * 12
  if(service.mode == 'days') return Math.max(1, Math.round(renewal / 30.4375))
  return null
}

const COST_FREQUENCIES = { 1: 'Mensual', 3: 'Trimestral', 6: 'Semestral', 12: 'Anual' }

export function costFrequencyLabel(months) {
  if(!months) return ''
  return COST_FREQUENCIES[months] || 'Cada ' + months + ' meses'
}
